/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraFoundationAdapterQueryChain,
  GamoraFoundationAdapterQueryChainResult,
} from './query/chain.js';
import {
  GamoraFoundationAdapterQueryClear,
  GamoraFoundationAdapterQueryClearResult,
} from './query/clear.js';
import {
  GamoraFoundationAdapterQueryGet,
  GamoraFoundationAdapterQueryGetExtractResult,
} from './query/get.js';
import {
  GamoraFoundationAdapterQueryHas,
  GamoraFoundationAdapterQueryHasResult,
} from './query/has.js';
import {
  GamoraFoundationAdapterQueryLock,
  GamoraFoundationAdapterQueryLockResult,
} from './query/lock.js';
import {
  GamoraFoundationAdapterQueryRemove,
  GamoraFoundationAdapterQueryRemoveResult,
} from './query/remove.js';
import {
  GamoraFoundationAdapterQueryUnlock,
  GamoraFoundationAdapterQueryUnlockResult,
} from './query/unlock.js';
import {
  GamoraFoundationAdapterQueryUpsert,
  GamoraFoundationAdapterQueryUpsertResult,
} from './query/upsert.js';
import { assert } from '../../utils/assert.js';
import { GamoraFoundationAdapterQuery } from './query.js';
import { GamoraUtilsStrand } from '../../utils/strand.js';
import { GamoraUUIDGuard } from '../../guards/uuid.js';

/**
 * Default namespace
 * @public
 */
export const GamoraFoundationAdapterDefaultNamespace = 'default';

/**
 * GamoraFoundationAdapter
 *
 * Abstract class for adapter types
 * @public
 */
export abstract class GamoraFoundationAdapter<TSubject = any, TArgs = any> {
  protected _$strand: GamoraUtilsStrand;
  protected _$args: TArgs;
  constructor(args: TArgs) {
    this._$args = args;
    this._$strand = new GamoraUtilsStrand();
  }

  /**
   * Args
   */
  get $args() {
    return this._$args;
  }

  /**
   * Expose strand
   */
  get $strand() {
    return this._$strand;
  }

  /**
   * Internal layer to execute a transaction
   * @param query The query
   */
  abstract $transaction<TQuery extends GamoraFoundationAdapterQuery>(
    query: TQuery,
  ): Promise<unknown>;

  /**
   * Execute query
   */
  async execute<TQuery extends GamoraFoundationAdapterQueryChain>(
    query: TQuery,
  ): Promise<GamoraFoundationAdapterQueryChainResult>;
  async execute<TQuery extends GamoraFoundationAdapterQueryClear>(
    query: TQuery,
  ): Promise<GamoraFoundationAdapterQueryClearResult>;
  async execute<
    TQuery extends GamoraFoundationAdapterQueryGet<TResult>,
    TResult = GamoraFoundationAdapterQueryGetExtractResult<TQuery>,
  >(query: TQuery): Promise<TResult>;
  async execute<TQuery extends GamoraFoundationAdapterQueryHas>(
    query: TQuery,
  ): Promise<GamoraFoundationAdapterQueryHasResult>;
  async execute<TQuery extends GamoraFoundationAdapterQueryRemove>(
    query: TQuery,
  ): Promise<GamoraFoundationAdapterQueryRemoveResult>;
  async execute<TQuery extends GamoraFoundationAdapterQueryUpsert>(
    query: TQuery,
  ): Promise<GamoraFoundationAdapterQueryUpsertResult>;
  async execute<TQuery extends GamoraFoundationAdapterQueryLock>(
    query: TQuery,
  ): Promise<GamoraFoundationAdapterQueryLockResult>;
  async execute<TQuery extends GamoraFoundationAdapterQueryUnlock>(
    query: TQuery,
  ): Promise<GamoraFoundationAdapterQueryUnlockResult>;
  async execute<TQuery extends GamoraFoundationAdapterQuery>(
    query: TQuery,
  ): Promise<void>;
  async execute<TQuery extends GamoraFoundationAdapterQuery>(
    query: TQuery,
  ): Promise<unknown> {
    return this.$transaction(query);
  }

  /**
   * Has
   * @param namespace The namespace
   * @param uuid The subject UUID
   */
  has(
    namespace: string,
    uuid: GamoraUUIDGuard,
    options?: any,
  ): GamoraFoundationAdapterQueryHas {
    assert(
      typeof namespace === 'string' && namespace.length > 0,
      'Invalid namespace argument: not a valid string.',
    );
    assert(
      uuid instanceof GamoraUUIDGuard,
      'Invalid uuid argument: not a GamoraUUIDGuard.',
    );
    return GamoraFoundationAdapterQueryHas.fromOperand(
      [namespace, uuid],
      options,
    );
  }

  /**
   * Upsert subject
   * @param namespace The namespace
   * @param uuid The subject UUID
   * @param subject The subject
   */
  upsert(
    namespace: string,
    uuid: GamoraUUIDGuard,
    subject: TSubject,
    options?: any,
  ): GamoraFoundationAdapterQueryUpsert<TSubject> {
    assert(
      typeof namespace === 'string' && namespace.length > 0,
      'Invalid namespace argument: not a valid string.',
    );
    assert(
      uuid instanceof GamoraUUIDGuard,
      'Invalid uuid argument: not a GamoraUUIDGuard.',
    );
    return GamoraFoundationAdapterQueryUpsert.fromOperand(
      [namespace, uuid, subject],
      options,
    );
  }

  /**
   * Removes subject
   * @param namespace The namespace
   * @param uuid The subject UUID
   */
  remove(
    namespace: string,
    uuid: GamoraUUIDGuard,
    options?: any,
  ): GamoraFoundationAdapterQueryRemove {
    assert(
      typeof namespace === 'string' && namespace.length > 0,
      'Invalid namespace argument: not a valid string.',
    );
    assert(
      uuid instanceof GamoraUUIDGuard,
      'Invalid uuid argument: not a GamoraUUIDGuard.',
    );
    return GamoraFoundationAdapterQueryRemove.fromOperand(
      [namespace, uuid],
      options,
    );
  }

  /**
   * Get subject
   * @param namespace The namespace
   * @param uuid The subject UUID
   */
  get(
    namespace: string,
    uuid: GamoraUUIDGuard,
    options?: any,
  ): GamoraFoundationAdapterQueryGet<TSubject> {
    assert(
      typeof namespace === 'string' && namespace.length > 0,
      'Invalid namespace argument: not a valid string.',
    );
    assert(
      uuid instanceof GamoraUUIDGuard,
      'Invalid uuid argument: not a GamoraUUIDGuard.',
    );
    return GamoraFoundationAdapterQueryGet.fromOperand(
      [namespace, uuid],
      options,
    );
  }

  /**
   * Locks subject
   * @param namespace The namespace
   * @param uuid The subject UUID
   * @param ttl The time to live for lock
   */
  lock(
    namespace: string,
    uuid: GamoraUUIDGuard,
    ttl = 30000,
    options?: any,
  ): GamoraFoundationAdapterQueryLock {
    assert(
      typeof namespace === 'string' && namespace.length > 0,
      'Invalid namespace argument: not a valid string.',
    );
    assert(
      uuid instanceof GamoraUUIDGuard,
      'Invalid uuid argument: not a GamoraUUIDGuard.',
    );
    assert(
      typeof ttl === 'number' && Number.isSafeInteger(ttl) && ttl > 0,
      'Invalid ttl argument: not a valid integer.',
    );
    return GamoraFoundationAdapterQueryLock.fromOperand(
      [namespace, uuid, ttl],
      options,
    );
  }

  /**
   * Unlock subject
   * @param namespace The namespace
   * @param uuid The subject UUID
   */
  unlock(
    namespace: string,
    uuid: GamoraUUIDGuard,
    options?: any,
  ): GamoraFoundationAdapterQueryUnlock {
    assert(
      typeof namespace === 'string' && namespace.length > 0,
      'Invalid namespace argument: not a valid string.',
    );
    assert(
      uuid instanceof GamoraUUIDGuard,
      'Invalid uuid argument: not a GamoraUUIDGuard.',
    );
    return GamoraFoundationAdapterQueryUnlock.fromOperand(
      [namespace, uuid],
      options,
    );
  }

  /**
   * Chain multiple queries into a single transaction
   * @param queries The queries to chain together
   */
  chain(
    queries: GamoraFoundationAdapterQuery[],
    options?: any,
  ): GamoraFoundationAdapterQueryChain {
    assert(
      Array.isArray(queries) && queries.length > 0,
      'Invalid queries argument: not an Array.',
    );
    assert(
      queries.every((query) => {
        return query instanceof GamoraFoundationAdapterQuery;
      }),
      'Invalid queries argument: a query is not a GamoraFoundationAdapterQuery.',
    );
    return GamoraFoundationAdapterQueryChain.fromOperand(queries, options);
  }

  /**
   * Clears a namespace
   * @param namespace The namespace
   */
  clear(namespace: string, options?: any): GamoraFoundationAdapterQueryClear {
    assert(
      typeof namespace === 'string' && namespace.length > 0,
      'Invalid namespace argument: not a valid string.',
    );
    return GamoraFoundationAdapterQueryClear.fromOperand([namespace], options);
  }
}
